import React, { useState, useMemo } from 'react';
import * as ReactRouterDom from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { 
  Calendar, ArrowLeft, Plus, ChevronRight, 
  Trash2, Edit3, Building2, 
  LayoutDashboard, Tag, Users2, Users, Upload, Zap, BarChart3, Copy,
  Clock
} from 'lucide-react';
import { formatDateSafe, formatFullDateSafe } from '../lib/utils';
import { Button } from '../components/ui/Button';
import { EngagementFormModal } from '../features/programs/EngagementFormModal';
import { SessionFormModal } from '../features/programs/SessionFormModal';
import { EngagementRosterManager } from '../features/programs/EngagementRosterManager';
import { EngagementDeleteDialog } from '../features/programs/EngagementDeleteDialog';

const { useParams, useNavigate, Link } = ReactRouterDom as any;

const getStatusStyles = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'in progress': return 'bg-blue-50 text-blue-600 border-blue-100';
    case 'delivered': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
    case 'in development': return 'bg-slate-50 text-slate-500 border-slate-100';
    case 'on hold': return 'bg-amber-50 text-amber-600 border-amber-100';
    case 'cancelled': return 'bg-rose-50 text-rose-600 border-rose-100';
    default: return 'bg-slate-50 text-slate-400 border-slate-100';
  }
};

const getSessionStatusStyles = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'upcoming': return 'bg-blue-50 text-blue-600 border-blue-100';
    case 'delivered': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
    case 'cancelled': return 'bg-rose-50 text-rose-600 border-rose-100';
    default: return 'bg-slate-50 text-slate-400 border-slate-100';
  }
};

const getServiceTypeLabel = (st: any) => {
  if (!st) return '';
  if (Array.isArray(st)) return st[0]?.label || '';
  return st.label || '';
};

const EngagementDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddSessionOpen, setIsAddSessionOpen] = useState(false);
  const [isRosterOpen, setIsRosterOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [sessionToClone, setSessionToClone] = useState<any>(null);

  const canImport = profile?.role === 'system_admin' || profile?.role === 'org_manager';

  const { data: engagement, isLoading: loadingEng, error: engError } = useQuery({
    queryKey: ['engagement-core', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('engagements')
        .select('*, audience_types(label), engagement_categories(label), regions:region_id(label)')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data;
    }
  });

  const { data: program } = useQuery({
    queryKey: ['program-lookup', engagement?.program_id],
    queryFn: async () => {
      const { data } = await supabase.from('programs').select('id, name').eq('id', engagement?.program_id).single();
      return data;
    },
    enabled: !!engagement?.program_id
  });

  const { data: sessions } = useQuery({
    queryKey: ['engagement-sessions', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          *, 
          host_org:host_org_id(name),
          session_service_types(id, label)
        `)
        .eq('engagement_id', id)
        .order('session_datetime', { ascending: false });
      if (error) throw error;
      
      return data || [];
    }
  });

  const { data: partners } = useQuery({
    queryKey: ['engagement-partners', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('engagement_organizations')
        .select('id, organization_id, role_id, organizations(id, name), organization_roles(label)')
        .eq('engagement_id', id);
      if (error) throw error;
      return data || [];
    }
  });

  const { data: rosterCount } = useQuery({
    queryKey: ['engagement-roster-count', id],
    queryFn: async () => {
      const { count } = await supabase.from('participation').select('*', { count: 'exact', head: true }).eq('engagement_id', id);
      return count || 0;
    }
  });

  const impactMetrics = useMemo(() => {
    if (!sessions || engagement?.tracking_mode !== 'headcount') return null;
    const counts = Array.isArray(sessions) 
      ? sessions.map(s => s.headcount_count || 0).filter(c => c > 0) 
      : [];
    const total = counts.reduce((sum, c) => sum + c, 0);
    const peak = counts.length > 0 ? Math.max(...counts) : 0;
    const avg = counts.length > 0 ? Math.round(total / counts.length) : 0;
    return { total, peak, avg };
  }, [sessions, engagement]);

  const totalCoachingHours = useMemo(() => {
    if (!sessions) return 0;
    return sessions.reduce((sum, s) => {
      const label = getServiceTypeLabel(s.session_service_types);
      const isCoaching = label.toLowerCase().includes('coaching');
      const hours = parseFloat(s.session_hours || 0) || 0;
      return isCoaching ? sum + hours : sum;
    }, 0);
  }, [sessions]);

  const totalInstructionalHours = useMemo(() => {
    if (!sessions) return 0;
    return sessions.reduce((sum, s) => {
      const label = getServiceTypeLabel(s.session_service_types);
      const isInstructional = label.toLowerCase().includes('instruction');
      const hours = parseFloat(s.session_hours || 0) || 0;
      return isInstructional ? sum + hours : sum;
    }, 0);
  }, [sessions]);

  const { data: groupedTags } = useQuery({
    queryKey: ['engagement-tags-detailed', id],
    queryFn: async () => {
      const { data } = await supabase
        .from('engagement_tag_links')
        .select(`
          tag:tag_id (
            label,
            group:group_id (label)
          )
        `)
        .eq('engagement_id', id);
      
      const groups: Record<string, string[]> = {};
      (data || []).forEach((d: any) => {
        const groupLabel = d.tag?.group?.label || 'Other';
        if (!groups[groupLabel]) groups[groupLabel] = [];
        groups[groupLabel].push(d.tag?.label);
      });
      return groups;
    },
    enabled: !!id
  });

  const [activeTab, setActiveTab] = useState<'sessions' | 'volunteering'>('sessions');

  const { data: allEngagementVolunteerLogs } = useQuery({
    queryKey: ['engagement-volunteer-logs', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('session_volunteer_logs')
        .select(`
          *,
          session:session_id(name, session_datetime),
          volunteer_types:volunteer_type_id(*),
          people:person_id(first_name, last_name)
        `)
        .in('session_id', sessions?.map(s => s.id) || []);
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: !!sessions && sessions.length > 0
  });

  if (loadingEng) return <div className="p-20 text-center uppercase font-bold text-slate-300 animate-pulse tracking-widest text-[10px]">Resolving engagement profile...</div>;
  if (engError || !engagement) return <div className="p-20 text-center text-rose-500 font-bold uppercase text-[10px]">Registry Error: Strategic Resource Not Resolved</div>;

  return (
    <div className="space-y-6 pb-20 max-w-7xl mx-auto animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(`/programs/${engagement.program_id}`)} className="p-2 hover:bg-white rounded-md border border-transparent hover:border-slate-200 transition-all text-slate-400">
            <ArrowLeft size={20} />
          </button>
          <div>
            <p className="text-[9px] font-bold text-blue-600 uppercase tracking-[0.2em] leading-none mb-1">{program?.name || 'Initiative'}</p>
            <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight leading-none mb-2">{engagement.name}</h1>
            <div className="flex items-center gap-2">
               <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-slate-900 text-[#FFDB00] text-[8px] font-black uppercase tracking-tighter border border-slate-700 shadow-sm">
                 <div className="w-1 h-1 rounded-full bg-[#FFDB00] animate-pulse" />
                 ENGAGEMENT
               </span>
               <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase border tracking-widest ${getStatusStyles(engagement.status || 'in development')}`}>
                 {engagement.status || 'IN DEVELOPMENT'}
               </span>
               <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest border border-slate-100 px-2 py-0.5 rounded bg-white">
                 {engagement.tracking_mode?.replace(/_/g, ' ')} MODE
               </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
           <Button variant="outline" onClick={() => setIsEditModalOpen(true)} icon={<Edit3 size={14} />}>Modify</Button>
           <button onClick={() => setIsDeleteDialogOpen(true)} className="p-2.5 text-rose-400 bg-white border border-slate-200 rounded-md hover:bg-rose-50 transition-all shadow-sm">
             <Trash2 size={16} />
           </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        {(engagement.engagement_categories?.label || engagement?.audience_types?.label) && (
          <div className="px-8 py-5 bg-white border-b border-slate-100 flex flex-wrap items-center gap-x-12 gap-y-4">
            {engagement.engagement_categories?.label && (
              <div className="flex items-center gap-3">
                 <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Structure:</span>
                 <span className="px-3 py-1.5 bg-slate-50 text-slate-900 text-[10px] font-bold uppercase rounded-md border border-slate-200 shadow-sm">{engagement.engagement_categories.label}</span>
              </div>
            )}
            {engagement?.audience_types?.label && (
               <div className="flex items-center gap-3">
                 <Users2 size={14} className="text-slate-400" />
                 <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Target Audience:</span>
                 <span className="px-3 py-1.5 bg-slate-50 text-slate-900 text-[10px] font-bold uppercase rounded-md border border-slate-200 shadow-sm">{engagement.audience_types.label}</span>
               </div>
            )}
          </div>
        )}

        {groupedTags && Object.keys(groupedTags).length > 0 && (
          <div className="px-8 py-4 bg-slate-50/50 border-b border-slate-100 flex flex-wrap items-center gap-x-8 gap-y-3">
            <div className="flex items-center gap-2">
              <Tag size={12} className="text-slate-400" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Engagement Tag(s):</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              {Object.entries(groupedTags).map(([group, labels], idx, arr) => (
                <div key={group} className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase text-slate-900 tracking-tight">{group}:</span>
                    <div className="flex flex-wrap items-center gap-1">
                      {labels.map((l, lIdx) => (
                        <React.Fragment key={l}>
                          <span className="px-2.5 py-1 bg-slate-900 text-[#FFDB00] text-[9px] font-bold uppercase rounded tracking-tight shadow-sm">
                            {l}
                          </span>
                          {lIdx < labels.length - 1 && <span className="text-[10px] text-slate-300">,</span>}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                  {idx < arr.length - 1 && <span className="text-slate-200 font-light ml-2">|</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="p-8 border-b border-slate-100 bg-white">
          <div className="space-y-2 max-w-4xl">
            <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Description</h3>
            <p className="text-slate-600 font-medium text-sm leading-relaxed tracking-wide">{engagement.description || 'No description defined.'}</p>
          </div>
        </div>

        <div className="p-8 bg-slate-50/30 grid grid-cols-2 md:grid-cols-5 gap-8 border-b border-slate-100">
          <div>
            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-[0.2em] block mb-1">start date</span>
            <span className="text-xs font-black text-slate-900 uppercase tracking-tighter">{formatDateSafe(engagement.start_date)}</span>
          </div>
          <div>
            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-[0.2em] block mb-1">end date</span>
            <span className="text-xs font-black text-slate-900 uppercase tracking-tighter">{formatDateSafe(engagement.end_date)}</span>
          </div>
          <div>
            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-[0.2em] block mb-1">capacity</span>
            <span className="text-xs font-black text-slate-900 uppercase">{engagement.capacity || 'None'}</span>
          </div>
          <div>
            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-[0.2em] block mb-1">Delivery Region</span>
            <span className="text-xs font-black text-slate-900 uppercase">{engagement.regions?.label || 'Not Specified'}</span>
          </div>
          <div className={`flex flex-col ${engagement.tracking_mode === 'headcount' ? 'invisible' : ''}`}>
             <span className="text-[8px] font-bold text-slate-400 uppercase tracking-[0.2em] block mb-1">Personnel Roster</span>
             <div className="flex items-center gap-2 min-h-[1.5rem]">
                <button onClick={() => setIsRosterOpen(true)} className="text-xs font-black text-blue-600 uppercase hover:underline">{rosterCount || 0} Enrolled</button>
                {engagement.tracking_mode === 'roster' && canImport && (
                  <Link to={`/import?engagementId=${id}`} className="p-1 hover:bg-slate-200 rounded transition-all text-slate-400" title="Bulk Import Roster">
                    <Upload size={14} />
                  </Link>
                )}
             </div>
          </div>
        </div>

        {engagement.tracking_mode === 'headcount' && (
          <div className="p-8 bg-white space-y-4 animate-in fade-in">
             <h3 className="text-[10px] font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
               <BarChart3 size={14} className="text-blue-600" /> Attendance Summary
             </h3>
             <div className="flex items-center gap-12">
                <div className="space-y-0.5">
                   <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Total</p>
                   <p className="text-xs font-black text-slate-900 uppercase tracking-tighter">{impactMetrics?.total || 0}</p>
                </div>
                <div className="space-y-0.5">
                   <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Peak</p>
                   <p className="text-xs font-black text-slate-900 uppercase tracking-tighter">{impactMetrics?.peak || 0}</p>
                </div>
                <div className="space-y-0.5">
                   <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Average</p>
                   <p className="text-xs font-black text-slate-900 uppercase tracking-tighter">{impactMetrics?.avg || 0}</p>
                </div>
             </div>
          </div>
        )}

        {(totalCoachingHours > 0 || totalInstructionalHours > 0) && (
          <div className={`p-8 bg-white border-t border-slate-100 space-y-4 animate-in fade-in`}>
             <h3 className="text-[10px] font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
               <Clock size={14} className="text-slate-900" /> Contact Summary
             </h3>
             <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:divide-x md:divide-slate-200">
                <div className="space-y-1">
                   <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest font-mono">Instructional Summary</p>
                   <p className="text-xs font-black text-blue-600 uppercase tracking-tighter">{totalInstructionalHours} Hours Delivered</p>
                </div>
                <div className="space-y-1 md:pl-8">
                   <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest font-mono">Coaching Summary</p>
                   <p className="text-xs font-black text-teal-600 uppercase tracking-tighter">{totalCoachingHours} Hours Delivered</p>
                </div>
                <div className="space-y-1 md:pl-8">
                   <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest font-mono">Total Hours</p>
                   <p className="text-xs font-black text-slate-900 uppercase tracking-tighter">{totalCoachingHours + totalInstructionalHours} Hours Delivered</p>
                </div>
             </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden h-fit">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/30">
            <h2 className="text-[10px] font-black text-slate-900 uppercase tracking-widest flex items-center gap-2"><Building2 size={14} /> Involved Organizations</h2>
          </div>
          <div className="divide-y divide-slate-50">
            {!partners || partners.length === 0 ? (
              <div className="p-10 text-center text-[9px] font-bold text-slate-300 uppercase tracking-widest italic">No Partners Logged</div>
            ) : (
              partners.map((eo: any) => (
                <Link key={eo.id} to={`/organizations/${eo.organization_id}`} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-all group">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-300 group-hover:bg-slate-900 group-hover:text-white transition-all"><Building2 size={14} /></div>
                    <span className="text-[11px] font-bold text-slate-900 uppercase truncate max-w-[140px] tracking-tight">{eo.organizations?.name || 'Unknown'}</span>
                  </div>
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-[8px] font-bold uppercase rounded border border-blue-100">{eo.organization_roles?.label || 'Linked Partner'}</span>
                </Link>
              ))
            )}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-6">
              <button 
                onClick={() => setActiveTab('sessions')}
                className={`text-[10px] font-black uppercase tracking-widest flex items-center gap-2 pb-2 transition-all border-b-2 ${activeTab === 'sessions' ? 'text-slate-900 border-slate-900' : 'text-slate-400 border-transparent hover:text-slate-600'}`}
              >
                <LayoutDashboard size={14} /> Sessions <span className="opacity-50">({sessions?.length || 0})</span>
              </button>
              <button 
                onClick={() => setActiveTab('volunteering')}
                className={`text-[10px] font-black uppercase tracking-widest flex items-center gap-2 pb-2 transition-all border-b-2 ${activeTab === 'volunteering' ? 'text-slate-900 border-slate-900' : 'text-slate-400 border-transparent hover:text-slate-600'}`}
              >
                <Users size={14} /> Volunteering <span className="opacity-50">({allEngagementVolunteerLogs?.reduce((sum, l) => sum + (l.volunteer_count || 0), 0) || 0})</span>
              </button>
            </div>
            {activeTab === 'sessions' && (
              <Button variant="primary" className="h-8 px-4 text-[9px] font-black bg-[#FFDB00] text-black hover:bg-[#E6C600] border-transparent shadow-sm" icon={<Plus size={14} />} onClick={() => setIsAddSessionOpen(true)}>New Session</Button>
            )}
          </div>

          {activeTab === 'sessions' ? (
            <div className="grid grid-cols-1 gap-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
              {Array.isArray(sessions) && sessions.map((session: any) => (
                <div key={session.id} onClick={() => navigate(`/sessions/${session.id}`)} className="bg-white p-5 border border-slate-200 hover:border-slate-900 rounded-md transition-all group flex items-center justify-between cursor-pointer shadow-sm hover:shadow-md">
                  <div className="flex items-center gap-5">
                    <div className="w-10 h-10 bg-slate-50 rounded-md flex items-center justify-center text-slate-300 group-hover:bg-slate-900 group-hover:text-yellow-400 transition-all shrink-0 border border-slate-100 shadow-inner"><Calendar size={18} /></div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 uppercase tracking-tight leading-none mb-2 group-hover:text-blue-600 transition-colors">{session.name || 'Operational Window'}</h4>
                      <div className="flex flex-wrap items-center gap-y-2 gap-x-4">
                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-widest"><Calendar size={12} className="text-slate-400" />{formatFullDateSafe(session.session_datetime)}</div>
                        <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase border tracking-widest ${getSessionStatusStyles(session.status || 'upcoming')}`}>{session.status || 'upcoming'}</span>
                        {engagement.tracking_mode === 'headcount' && (session.headcount_count || 0) > 0 && (
                          <span className="flex items-center gap-1 text-[9px] font-black uppercase text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100"><Zap size={10} fill="currentColor" /> Reach: {session.headcount_count}</span>
                        )}
                        {parseFloat(String(session.session_hours || 0)) > 0 && (
                          (() => {
                            const labelText = getServiceTypeLabel(session.session_service_types) || 'Session';
                            const isInstructional = labelText.toLowerCase().includes('instruction');
                            const isCoaching = labelText.toLowerCase().includes('coaching');
                            const badgeColorClass = isInstructional 
                              ? "text-blue-600 bg-blue-50 border-blue-100" 
                              : isCoaching 
                              ? "text-teal-600 bg-teal-50 border-teal-100"
                              : "text-slate-600 bg-slate-50 border-slate-100";
                            return (
                              <span className={`flex items-center gap-1 text-[9px] font-black uppercase px-2 py-0.5 rounded border ${badgeColorClass}`}>
                                <Clock size={10} /> {labelText}: {session.session_hours} Hrs
                              </span>
                            );
                          })()
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={(e) => { e.stopPropagation(); setSessionToClone(session); }}
                      className="p-2 text-slate-200 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                      title="Clone Session"
                    >
                      <Copy size={16} />
                    </button>
                  </div>
                </div>
              ))}
              {(!sessions || sessions.length === 0) && (
                <div className="p-12 text-center border border-dashed border-slate-200 rounded text-[10px] font-black uppercase text-slate-300 tracking-widest">No sessions scheduled</div>
              )}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="p-6 border-b border-slate-100 bg-slate-50/50">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">Full Volunteering Log</h3>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Total Hours</p>
                      <p className="text-xs font-black text-slate-900">{allEngagementVolunteerLogs?.reduce((sum, l) => sum + (l.total_hours || 0), 0) || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Total Volunteers</p>
                      <p className="text-xs font-black text-slate-900">{allEngagementVolunteerLogs?.reduce((sum, l) => sum + (l.volunteer_count || 0), 0) || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {allEngagementVolunteerLogs?.map((log: any) => (
                  <div 
                    key={log.id} 
                    className={`p-5 flex items-center justify-between transition-all ${log.person_id ? 'hover:bg-slate-50 cursor-pointer' : 'bg-white'}`}
                    onClick={() => log.person_id && navigate(`/people/${log.person_id}`)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center text-slate-400 font-bold text-[9px]">
                        {log.person_id ? 'PER' : 'GRP'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-bold text-slate-900 uppercase tracking-tight">
                            {log.person_id ? `${log.people?.first_name} ${log.people?.last_name}` : `${log.volunteer_count} Volunteers`}
                          </span>
                          <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 text-[8px] font-black uppercase rounded border border-blue-100">
                            {log.volunteer_types?.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                          <span className="text-blue-600 font-black">{log.session?.name}</span>
                          <span className="opacity-40">|</span>
                          <span>{formatDateSafe(log.session?.session_datetime)}</span>
                          <span className="opacity-40">|</span>
                          <span>{log.total_hours} Hours</span>
                        </div>
                      </div>
                    </div>
                    <ChevronRight size={14} className="text-slate-300" />
                  </div>
                ))}
                {(!allEngagementVolunteerLogs || allEngagementVolunteerLogs.length === 0) && (
                  <div className="p-20 text-center text-[10px] font-black text-slate-300 uppercase tracking-widest italic">
                    No volunteering history found for this engagement
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {isEditModalOpen && <EngagementFormModal programId={engagement.program_id} engagement={engagement} onClose={() => setIsEditModalOpen(false)} />}
      {isAddSessionOpen && <SessionFormModal engagementId={id!} onClose={() => setIsAddSessionOpen(false)} />}
      {sessionToClone && <SessionFormModal engagementId={id!} session={sessionToClone} isClone={true} onClose={() => setSessionToClone(null)} />}
      {isRosterOpen && <EngagementRosterManager engagementId={id!} onClose={() => setIsRosterOpen(false)} />}
      {isDeleteDialogOpen && (
        <EngagementDeleteDialog 
          engagementId={id!} 
          engagementName={engagement.name}
          onClose={() => setIsDeleteDialogOpen(false)} 
        />
      )}
    </div>
  );
};

export default EngagementDetail;